import { logErrorAndEventuallyReportToBimBamBoom } from "../utils/logging"
import { FeatureFlagValues, parseFlag } from "./types"

declare global {
  interface Window {
    __DEFAULT_FEATURE_FLAGS__: undefined | Partial<FeatureFlagValues>
  }
}

/**
 * Flags an environment variable can switch on at build time, and the value it switches them to.
 *
 * Holds flag *values* rather than booleans so a flag that is not a boolean can use this too —
 * `agentChat` is `{ enabled }`, and a bare `true` would fail its schema. `undefined` means the
 * variable was not set, and only turning a flag *on* is supported: absent already means off, and an
 * env var that could force a flag off would silently outrank a deployment that had turned it on.
 *
 * Set these in the `.env` file the dev script loads — `front-end/local-dev.env` — not in a
 * production build, where the deployment's own flags file is the intended source.
 */
const BUILD_TIME_OVERRIDES: Partial<FeatureFlagValues> = {
  localMode: getBuildTimeFlag('REACT_APP_LOCAL_MODE') || undefined,
  inspectorGadget: getBuildTimeFlag('REACT_APP_INSPECTOR_GADGET') || undefined,
  singleLogin: getBuildTimeFlag('REACT_APP_SINGLE_LOGIN_PREVIEW') || undefined,
  agentChat: getBuildTimeFlag('REACT_APP_AGENT_CHAT') ? { enabled: true } : undefined,
  codeWidget: codeWidgetOverride(),
}

/**
 * The CodeWidget, which needs two variables rather than one.
 *
 * `crossOriginRepoUrlAllowed` decides whether the widget runs when the app's repoURL is on a
 * different origin from the page, so it gets its own name instead of riding along with "switch the
 * widget on". It has no effect unless `REACT_APP_CODE_WIDGET` is set, because an override replaces
 * the whole flag object rather than merging into it — which also means enabling the widget without
 * the second variable gives the safe value, not the previous one.
 */
function codeWidgetOverride(): FeatureFlagValues['codeWidget'] | undefined {
  if (!getBuildTimeFlag('REACT_APP_CODE_WIDGET')) return undefined
  return {
    enabled: true,
    crossOriginRepoUrlAllowed: getBuildTimeFlag('REACT_APP_CODE_WIDGET_CROSS_ORIGIN'),
  }
}

const DEFAULT_FALLBACK: FeatureFlagValues = {
  singleLogin: false,
  localMode: false,
  inspectorGadget: false,
  experimentalConfiguratorUx: false,
  basicWidgetSettingsByDefault: false,
  selectableWidgets: {
    names: {
      allowed: [],
      blocked: [],
    },
    categories: {
      allowed: [],
      blocked: [],
    },
    defaultPolicy: 'allowed',
  },
  codeWidget: {
    enabled: false,
    crossOriginRepoUrlAllowed: false,
  },
  // Absent means off, which is the direction that cannot surprise anyone: a deployment with no
  // model host must not offer an agent, and a deployment that has one says so explicitly.
  agentChat: {
    enabled: false,
  }
}

export function getDefault<K extends keyof FeatureFlagValues>(flag: K): FeatureFlagValues[K] {
  const fromEnvironment = BUILD_TIME_OVERRIDES[flag]
  if (fromEnvironment !== undefined) return fromEnvironment as FeatureFlagValues[K]

  const fallback =  DEFAULT_FALLBACK[flag]
  const defaultValue = window.__DEFAULT_FEATURE_FLAGS__?.[flag]
  if (defaultValue == null) return fallback
  try {
    parseFlag(flag, defaultValue, (e) => {
      logErrorAndEventuallyReportToBimBamBoom(`window.__DEFAULT_FEATURE_FLAGS__.${flag} was found not to match schema`, e)
    })
    return defaultValue
  } catch (e) {
    logErrorAndEventuallyReportToBimBamBoom(`window.__DEFAULT_FEATURE_FLAGS__.${flag} was found not to match schema`, e)
    return fallback
  }
}

function getBuildTimeFlag(envKey: string): boolean {
  const buildTimeValue = process.env[envKey]
  if (buildTimeValue == null) return false
  try {
    return JSON.parse(buildTimeValue) === true
  } catch {
    return false
  }
}
